/**
 * LITOS scroll reveal — fade up on enter, once.
 *
 * Opt in by putting `data-litos-reveal` on a section root and the class
 * `litos-reveal` on the elements that should animate. Stagger with an inline
 * `--reveal-delay`. Styles live in litos-quicklaunch.css.
 *
 * Reveal is one-way on purpose: once an element has animated in it keeps
 * `is-visible`, so scrolling back up never plays it in reverse.
 *
 * Note: sections also print a tiny inline script that adds `is-reveal-ready`
 * while the HTML is parsed. That is what keeps the hidden state from ever
 * applying when JS is off — this file only re-adds it for theme editor
 * re-renders, where inline scripts don't run.
 */
(function () {
  const CONTAINER = '[data-litos-reveal]';
  const ITEM = '.litos-reveal';

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function bind(container) {
    if (container.dataset.litosRevealBound === 'true') return;
    container.dataset.litosRevealBound = 'true';

    const items = container.querySelectorAll(ITEM);
    if (!items.length) return;

    // No observer or the visitor asked for less motion — show everything as-is.
    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      container.classList.remove('is-reveal-ready');
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    container.classList.add('is-reveal-ready');

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    items.forEach((el) => observer.observe(el));
  }

  function scan(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (root.matches && root.matches(CONTAINER)) bind(root);
    root.querySelectorAll(CONTAINER).forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document));
  } else {
    scan(document);
  }

  // Theme editor: a re-rendered section arrives with a fresh DOM subtree.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
